import React, { useState } from 'react';
import { ArrowRight, Sparkles, CheckCircle2, Zap, ShieldCheck, Play, Layers, FileCode, Eye } from 'lucide-react';
import { COMPANY_INFO } from '../data/companyData';
import { useLanguage } from '../context/LanguageContext';

interface HeroProps {
  onGetStarted: () => void;
  onContactUs: () => void;
  onOpenEstimator: () => void;
  onOpenSecurityAudit?: () => void;
}

export const Hero: React.FC<HeroProps> = ({ onGetStarted, onContactUs, onOpenEstimator, onOpenSecurityAudit }) => {
  const [activeTab, setActiveTab] = useState<'traditional' | 'asap'>('asap');
  const { t } = useLanguage();

  return (
    <section id="hero" className="relative overflow-hidden bg-gradient-to-b from-slate-50 via-blue-50/40 to-white pt-8 sm:pt-16 pb-16 sm:pb-24">
      {/* Background Animated Gradient Mesh & Particles Accent */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-7xl h-[600px] opacity-40 pointer-events-none">
        <div className="absolute -top-24 left-1/4 w-96 h-96 bg-blue-400/30 rounded-full blur-3xl animate-pulse duration-10000" />
        <div className="absolute top-20 right-1/4 w-96 h-96 bg-purple-400/25 rounded-full blur-3xl animate-pulse duration-7000" />
        <div className="absolute top-60 left-1/3 w-80 h-80 bg-sky-300/30 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          
          {/* Left Column: Headlines & Call to Actions */}
          <div className="lg:col-span-7 space-y-6 text-center lg:text-left">
            
            {/* Top Pill Badge */}
            <div className="inline-flex items-center gap-2.5 px-4 py-1.5 rounded-full bg-gradient-to-r from-blue-50/90 via-indigo-50/90 to-purple-50/90 border border-blue-200/80 text-blue-950 text-xs sm:text-sm font-semibold shadow-sm shadow-blue-500/5 hover:border-blue-300 transition-all">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-500 opacity-75" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-indigo-600" />
              </span>
              <Sparkles className="w-4 h-4 text-purple-600 animate-pulse shrink-0" />
              <span className="tracking-wide font-bold bg-gradient-to-r from-blue-900 via-indigo-900 to-purple-900 bg-clip-text text-transparent">
                {t('hero_badge', 'AI-Driven Publishing Solutions')}
              </span>
            </div>

            {/* Main Headline */}
            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black text-slate-900 tracking-tight leading-[1.14] lg:leading-[1.12]">
              {t('hero_title_1', 'Transforming Publishing')}{' '}
              <span className="bg-gradient-to-r from-blue-700 via-blue-800 to-indigo-900 bg-clip-text text-transparent">
                {t('hero_title_2', 'Through AI & Creative Excellence')}
              </span>
            </h1>

            {/* Subtitle Paragraph */}
            <p className="text-base sm:text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto lg:mx-0 font-normal">
              {t('hero_subtitle', 'Delivering world-class publishing solutions through Adobe Creative Cloud, XML, Accessibility, Copy Editing, and intelligent production workflows.')}
            </p>

            {/* Call To Action Buttons */}
            <div className="pt-2 flex flex-wrap sm:flex-nowrap items-center justify-center lg:justify-start gap-2 sm:gap-2.5">
              <button
                onClick={onGetStarted}
                className="px-4 py-2.5 rounded-xl font-bold text-xs sm:text-sm text-white bg-blue-600 hover:bg-blue-700 shadow-md shadow-blue-600/20 hover:scale-[1.01] transition-all flex items-center justify-center gap-1.5 whitespace-nowrap shrink-0 group"
              >
                <span>{t('btn_get_started', 'Get Started')}</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </button>

              {onOpenSecurityAudit && (
                <button
                  onClick={onOpenSecurityAudit}
                  className="px-3.5 py-2.5 rounded-xl font-semibold text-xs sm:text-sm text-emerald-800 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 transition-all flex items-center justify-center gap-1.5 whitespace-nowrap shrink-0"
                >
                  <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
                  <span>Security & Audit</span>
                </button>
              )}
            </div>

            {/* Key Trust Signals */}
            <div className="pt-6 border-t border-slate-200/80 grid grid-cols-3 gap-2 sm:gap-4 max-w-xl mx-auto lg:mx-0">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                <span className="text-xs font-semibold text-slate-700">100% WCAG PDF/UA</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-blue-600 shrink-0" />
                <span className="text-xs font-semibold text-slate-700">JATS/BITS XML</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-purple-600 shrink-0" />
                <span className="text-xs font-semibold text-slate-700">InDesign Mastery</span>
              </div>
            </div>

          </div>

          {/* Right Column: Interactive Workflow Visualizer Showcase */}
          <div className="lg:col-span-5">
            <div className="relative bg-white/90 backdrop-blur-md rounded-2xl p-6 sm:p-8 shadow-2xl border border-slate-200/80 hover:border-blue-300 transition-all">
              
              {/* Header inside Showcase Card */}
              <div className="flex items-center justify-between border-b border-slate-100 pb-4 mb-5">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-400" />
                  <div className="w-3 h-3 rounded-full bg-amber-400" />
                  <div className="w-3 h-3 rounded-full bg-emerald-400" />
                  <span className="ml-2 text-xs font-bold text-slate-500 uppercase tracking-wider">
                    Publishing Workflow Engine
                  </span>
                </div>
                <span className="px-2.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[11px] font-bold">
                  AI Accelerated
                </span>
              </div>

              {/* Toggle comparison between ASAP AI Workflow vs Traditional */}
              <div className="bg-slate-100 p-1 rounded-xl flex mb-6">
                <button
                  onClick={() => setActiveTab('asap')}
                  className={`flex-1 py-2 text-xs font-bold rounded-lg transition-all ${
                    activeTab === 'asap'
                      ? 'bg-blue-600 text-white shadow-md'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  ⚡ ASAP AI Workflow (60% Faster)
                </button>
                <button
                  onClick={() => setActiveTab('traditional')}
                  className={`flex-1 py-2 text-xs font-bold rounded-lg transition-all ${
                    activeTab === 'traditional'
                      ? 'bg-slate-800 text-white shadow-md'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  🐢 Traditional Workflow
                </button>
              </div>

              {/* Dynamic Content display */}
              {activeTab === 'asap' ? (
                <div className="space-y-4 animate-in fade-in duration-300">
                  <div className="p-3.5 rounded-xl bg-blue-50/90 border border-blue-200/80 flex items-center justify-between shadow-2xs">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-blue-600 text-white shadow-sm">
                        <Layers className="w-4 h-4" />
                      </div>
                      <div>
                        <p className="text-xs font-extrabold text-slate-900">Typesetting & Composition</p>
                        <p className="text-[11px] font-semibold text-slate-700">Auto InDesign Master & Grid AI</p>
                      </div>
                    </div>
                    <span className="text-xs font-bold text-blue-800 bg-white px-2 py-1 rounded-md border border-blue-200 shadow-2xs">
                      Day 1 - 2
                    </span>
                  </div>

                  <div className="p-3.5 rounded-xl bg-purple-50/90 border border-purple-200/80 flex items-center justify-between shadow-2xs">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-purple-600 text-white shadow-sm">
                        <FileCode className="w-4 h-4" />
                      </div>
                      <div>
                        <p className="text-xs font-extrabold text-slate-900">XML Schema & EPUB 3</p>
                        <p className="text-[11px] font-semibold text-slate-700">Automated DTD Rules Verification</p>
                      </div>
                    </div>
                    <span className="text-xs font-bold text-purple-800 bg-white px-2 py-1 rounded-md border border-purple-200 shadow-2xs">
                      Instant DTD
                    </span>
                  </div>

                  <div className="p-3.5 rounded-xl bg-emerald-50/90 border border-emerald-200/80 flex items-center justify-between shadow-2xs">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-emerald-600 text-white shadow-sm">
                        <Eye className="w-4 h-4" />
                      </div>
                      <div>
                        <p className="text-xs font-extrabold text-slate-900">PDF/UA Accessibility</p>
                        <p className="text-[11px] font-semibold text-slate-700">PAC 2021 & Screen Reader Certified</p>
                      </div>
                    </div>
                    <span className="text-xs font-bold text-emerald-800 bg-white px-2 py-1 rounded-md border border-emerald-200 shadow-2xs">
                      100% Pass
                    </span>
                  </div>

                  <div className="pt-3 border-t border-slate-200 flex items-center justify-between text-xs text-slate-700">
                    <span className="font-semibold">Overall Turnaround:</span>
                    <span className="font-black text-blue-800 text-sm">3 - 5 Days Average</span>
                  </div>
                </div>
              ) : (
                <div className="space-y-4 animate-in fade-in duration-300">
                  <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-between opacity-90 shadow-2xs">
                    <div>
                      <p className="text-xs font-extrabold text-slate-900">Manual Typesetting</p>
                      <p className="text-[11px] font-semibold text-slate-700">Manual grid adjustment & manual proofs</p>
                    </div>
                    <span className="text-xs font-bold text-slate-700 bg-slate-200 px-2 py-1 rounded-md border border-slate-300/60">
                      10 - 15 Days
                    </span>
                  </div>

                  <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-between opacity-90 shadow-2xs">
                    <div>
                      <p className="text-xs font-extrabold text-slate-900">Manual Tagging & XML</p>
                      <p className="text-[11px] font-semibold text-slate-700">Line-by-line coding & re-keying errors</p>
                    </div>
                    <span className="text-xs font-bold text-slate-700 bg-slate-200 px-2 py-1 rounded-md border border-slate-300/60">
                      7 - 10 Days
                    </span>
                  </div>

                  <div className="pt-3 border-t border-slate-200 flex items-center justify-between text-xs text-slate-700">
                    <span className="font-semibold">Traditional Turnaround:</span>
                    <span className="font-black text-slate-800 text-sm">3 - 4 Weeks</span>
                  </div>
                </div>
              )}

              {/* Bottom Quick Callout */}
              <div className="mt-6 p-4 rounded-xl bg-gradient-to-r from-blue-900 to-indigo-900 text-white flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold text-blue-200">Ready to speed up production?</p>
                  <p className="text-sm font-bold">Request a Free Sample Conversion</p>
                </div>
                <button
                  onClick={onGetStarted}
                  className="px-3.5 py-1.5 rounded-lg bg-white text-blue-900 font-extrabold text-xs hover:bg-blue-50 transition-colors shrink-0"
                >
                  Try Now
                </button>
              </div>

            </div>
          </div>

        </div>
      </div>
    </section>
  );
};
